import { useEffect, useState } from "react";
import { Phone, Globe, MessageCircle, AlertTriangle, Heart, Users, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const fallbackCrisis = [
  {
    name: "988 Suicide & Crisis Lifeline",
    phone: "988",
    href: "tel:988",
    web: "https://988lifeline.org",
    description: "Free, confidential, 24/7 support for anyone in suicidal crisis or emotional distress. Call or text 988.",
  },
  {
    name: "SAMHSA National Helpline",
    phone: "1-800-662-HELP (4357)",
    href: "tel:18006624357",
    web: "https://www.samhsa.gov/find-help/national-helpline",
    description: "Free, confidential, 24/7 treatment referral and information service for individuals and families facing mental and/or substance use disorders.",
  },
  {
    name: "Florida 211 - United Way",
    phone: "Dial 211",
    href: "tel:211",
    web: "https://www.211.org",
    description: "Statewide referral service connecting Floridians to local social services, food, housing, recovery resources, and more.",
  },
  {
    name: "Crisis Text Line",
    phone: "Text HOME to 741741",
    href: "sms:741741&body=HOME",
    web: "https://www.crisistextline.org",
    description: "Free, 24/7 crisis counseling by text from trained Crisis Counselors.",
  },
];

const fallbackSupport = [
  {
    name: "NAMI - National Alliance on Mental Illness",
    web: "https://www.nami.org",
    description: "The largest grassroots mental health organization in the U.S. Education, support groups, advocacy, and a HelpLine: 1-800-950-NAMI (6264).",
  },
  {
    name: "NAMI Greater Orlando",
    web: "https://namigo.org",
    description: "Local NAMI affiliate serving Orange, Osceola, and Seminole counties - free family-to-family courses, support groups, and presentations.",
  },
  {
    name: "Al-Anon Family Groups",
    web: "https://al-anon.org",
    description: "Mutual support for families and friends of people with alcohol use disorder. Meetings in-person, online, and by phone.",
  },
  {
    name: "Nar-Anon Family Groups",
    web: "https://www.nar-anon.org",
    description: "Mutual support program for family members and friends of those with drug addiction.",
  },
  {
    name: "Grief Recovery After a Substance Passing (GRASP)",
    web: "https://grasphelp.org",
    description: "Support and resources for individuals who have experienced the loss of a loved one to substance use disorder.",
  },
  {
    name: "Shatterproof",
    web: "https://www.shatterproof.org",
    description: "National nonprofit dedicated to reversing the addiction crisis - advocacy, treatment locator, and stigma-reduction campaigns.",
  },
];

const fallbackHarmReduction = [
  {
    name: "SAMHSA Treatment Locator",
    web: "https://findtreatment.gov",
    description: "Confidential, anonymous resource to find substance use treatment facilities in the U.S. by ZIP code.",
  },
  {
    name: "Florida Department of Children and Families - Substance Abuse",
    web: "https://www.myflfamilies.com/services/samh",
    description: "Florida's state agency overseeing publicly funded substance abuse and mental health services.",
  },
];

const ResourceCard = ({ item, type }) => (
  <article className="bg-white border-2 border-[var(--rts-blue-pale)] hover:border-[var(--rts-blue)] rounded-2xl p-6 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`resource-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}>
    <h3 className="font-display text-xl text-[var(--rts-ink)] leading-tight mb-3">{item.name}</h3>
    <p className="text-sm text-[var(--rts-ink-soft)] leading-relaxed mb-4">{item.description}</p>
    <div className="flex flex-wrap gap-2">
      {item.phone && (
        <a href={item.href} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[var(--rts-red)] hover:bg-[var(--rts-red-deep)] text-white text-sm font-bold transition min-h-[44px]">
          <Phone className="w-3.5 h-3.5" /> {item.phone}
        </a>
      )}
      {item.web && (
        <a href={item.web} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[var(--rts-blue-pale)] hover:bg-[var(--rts-blue)] hover:text-white text-[var(--rts-blue-deep)] text-sm font-bold transition min-h-[44px]">
          <Globe className="w-3.5 h-3.5" /> Visit site <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  </article>
);

export default function Resources() {
  const [crisis, setCrisis] = useState(fallbackCrisis);
  const [support, setSupport] = useState(fallbackSupport);
  const [harmReduction, setHarmReduction] = useState(fallbackHarmReduction);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/resources");
        if (!Array.isArray(data) || data.length === 0) return;
        const map = { crisis: [], support: [], treatment: [] };
        for (const r of data) {
          const item = {
            name: r.name || "",
            description: r.description || "",
            phone: r.phone || "",
            href: r.phone_href || "",
            web: r.web || "",
          };
          (map[r.category] || map.crisis).push(item);
        }
        if (map.crisis.length) setCrisis(map.crisis);
        if (map.support.length) setSupport(map.support);
        if (map.treatment.length) setHarmReduction(map.treatment);
      } catch {}
    })();
  }, []);

  return (
    <div data-testid="resources-page">
      {/* Hero */}
      <section className="bg-[var(--rts-blue-pale)] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-red)] mb-4">
            Resources
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--rts-ink)] leading-[1.05]">
            Help is closer than <span className="italic text-[var(--rts-blue-deep)]">you think.</span>
          </h1>
          <p className="mt-5 text-lg text-[var(--rts-ink-soft)] max-w-3xl leading-relaxed">
            Whether you're in crisis right now, supporting a loved one, or grieving a loss - these resources are free, confidential, and ready when you are.
          </p>
        </div>
      </section>

      {/* Emergency banner */}
      <section className="bg-[var(--rts-red)] text-white py-6" data-testid="emergency-banner">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <AlertTriangle className="w-7 h-7 shrink-0" />
          <p className="flex-1 text-sm sm:text-base">
            <strong>If you or someone you love is in immediate danger, call 911.</strong> For a mental health or substance use crisis, call or text <a href="tel:988" className="underline font-bold">988</a> - the Suicide & Crisis Lifeline.
          </p>
        </div>
      </section>

      {/* Crisis */}
      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[var(--rts-red)] grid place-items-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-[var(--rts-ink)] leading-tight">Crisis Support</h2>
              <p className="text-sm text-[var(--rts-muted)]">24/7 - Free - Confidential</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5" data-testid="crisis-grid">
            {crisis.map((c) => <ResourceCard key={c.name} item={c} type="crisis" />)}
          </div>
        </div>
      </section>

      {/* Support */}
      <section className="bg-[var(--rts-blue-pale)] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[var(--rts-blue-deep)] grid place-items-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-[var(--rts-ink)] leading-tight">Family & Community Support</h2>
              <p className="text-sm text-[var(--rts-muted)]">For families, siblings, and friends walking this road too.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5" data-testid="support-grid">
            {support.map((s) => <ResourceCard key={s.name} item={s} type="support" />)}
          </div>
        </div>
      </section>

      {/* Naloxone distribution callout */}
      <section className="bg-[var(--rts-red)] text-white py-14" data-testid="naloxone-callout">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-white/85 mb-2">PARS Distributes Naloxone</p>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-3">Free Naloxone (Narcan). It saves lives.</h2>
            <p className="text-white/90 leading-relaxed">
              PARS provides free naloxone through community distribution events. If you, your organization, school, church, or workplace would like to host a distribution or request kits, reach out - we'll bring the supplies and the training.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-3 md:justify-end">
            <a href="/events" className="inline-flex items-center gap-2 bg-white text-[var(--rts-red)] hover:bg-white/90 px-6 py-3 rounded-full font-bold shadow-lg transition hover:-translate-y-0.5" data-testid="naloxone-see-events">
              See Distribution Events
            </a>
            <a href="/connect-with-us" className="inline-flex items-center gap-2 bg-black/20 hover:bg-black/30 border border-white/30 px-6 py-3 rounded-full font-bold transition" data-testid="naloxone-contact-us">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Treatment + Harm reduction */}
      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[var(--rts-blue)] grid place-items-center">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-[var(--rts-ink)] leading-tight">Treatment & Harm Reduction</h2>
              <p className="text-sm text-[var(--rts-muted)]">Find treatment near you. Get free naloxone. Save a life.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="treatment-grid">
            {harmReduction.map((h) => <ResourceCard key={h.name} item={h} type="treatment" />)}
          </div>
        </div>
      </section>

      {/* Footer disclaimer */}
      <section className="bg-[var(--rts-ink)] text-white py-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm text-white/70 leading-relaxed">
            PARS is not a treatment provider. The resources above are curated to help - please reach out to a qualified professional for clinical advice. If you know of a Florida-based resource we should add, <a className="underline font-semibold text-[var(--rts-blue-light)]" href="mailto:ReversingStigma@gmail.com">let us know</a>.
          </p>
        </div>
      </section>
    </div>
  );
}
